import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { AuthContextType, User, LoginRequest, RegisterRequest, GoogleCountrySelectionOption, isCountrySelectionRequired } from '../types';
import { apiService } from '../services/apiService';

// Estado inicial
interface AuthState {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: AuthState = {
  user: null,
  token: null,
  isLoading: false,
  error: null,
};

// Acciones del reducer
type AuthAction =
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: { user: User; token: string } }
  | { type: 'LOGIN_FAILURE'; payload: string }
  | { type: 'REGISTER_START' }
  | { type: 'REGISTER_SUCCESS' }
  | { type: 'REGISTER_FAILURE'; payload: string }
  | { type: 'LOGOUT' }
  // Neither success nor failure - the Google login flow paused for the
  // dual-account company-picker (CLAUDE.md §1/§2). Stops the loading
  // spinner without setting an error or touching user/token.
  | { type: 'LOGIN_AWAITING_SELECTION' }
  | { type: 'SET_USER'; payload: User }
  | { type: 'SET_TOKEN'; payload: string }
  | { type: 'SET_ERROR'; payload: string }
  | { type: 'CLEAR_ERROR' };

// Reducer
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'LOGIN_START':
    case 'REGISTER_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };

    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isLoading: false,
        error: null,
      };

    case 'LOGIN_FAILURE':
    case 'REGISTER_FAILURE':
      return {
        ...state,
        isLoading: false,
        error: action.payload,
      };

    case 'REGISTER_SUCCESS':
      return {
        ...state,
        isLoading: false,
        error: null,
      };

    case 'LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        isLoading: false,
        error: null,
      };

    case 'LOGIN_AWAITING_SELECTION':
      return {
        ...state,
        isLoading: false,
        error: null,
      };

    case 'SET_USER':
      return {
        ...state,
        user: action.payload,
      };

    case 'SET_TOKEN':
      return {
        ...state,
        token: action.payload,
      };

    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
      };

    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };

    default:
      return state;
  }
};

// Crear contexto
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Hook para usar el contexto
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Provider del contexto
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Restore synchronously on the first render: restoring only in the effect
  // below left user=null for one render, so ProtectedRoute bounced every
  // deep link / reload (e.g. the ML OAuth return to /ml?code=) to /login
  // and on to /dashboard.
  const [state, dispatch] = useReducer(authReducer, initialState, (init): AuthState => {
    try {
      const storedUser = localStorage.getItem('user');
      const storedToken = localStorage.getItem('token');
      if (storedUser && storedToken) {
        return { ...init, user: JSON.parse(storedUser), token: storedToken };
      }
    } catch {
      // corrupt storage: the effect below clears it
    }
    return init;
  });

  // Restaurar usuario del localStorage al cargar
  useEffect(() => {
    const restoreAuth = async () => {
      const storedUser = localStorage.getItem('user');
      const storedToken = localStorage.getItem('token');

      if (storedUser && storedToken) {
        try {
          const user = JSON.parse(storedUser);
          apiService.setAuthToken(storedToken);
          dispatch({ type: 'SET_USER', payload: user });
          dispatch({ type: 'SET_TOKEN', payload: storedToken });
        } catch (error) {
          localStorage.removeItem('user');
          localStorage.removeItem('token');
          dispatch({ type: 'LOGOUT' });
        }
      }
    };

    restoreAuth();
  }, []);

  // Función de login
  const login = async (credentials: LoginRequest): Promise<void> => {
    dispatch({ type: 'LOGIN_START' });
    try {
      const tokenResponse = await apiService.auth.login(credentials);
      const token = tokenResponse.access_token;
      
      // Configurar token en el servicio API
      apiService.setAuthToken(token);
      
      // Obtener datos del usuario
      const user = await apiService.auth.getCurrentUser();
      
      // Guardar user Y token en localStorage
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('token', token);
      
      dispatch({ 
        type: 'LOGIN_SUCCESS', 
        payload: { user, token } 
      });
    } catch (error: any) {
      let errorMessage = 'Error de login';
      
      if (error.response?.data) {
        const data = error.response.data;
        
        // Si es un array de errores de validación
        if (Array.isArray(data.detail)) {
          errorMessage = data.detail.map((err: any) => err.msg).join(', ');
        }
        // Si es un string simple
        else if (typeof data.detail === 'string') {
          errorMessage = data.detail;
        }
        // Si es un objeto con mensaje
        else if (data.message) {
          errorMessage = data.message;
        }
        // Si es un error de red o conexión
        else if (error.message) {
          errorMessage = error.message;
        }
      }
      
      dispatch({ type: 'LOGIN_FAILURE', payload: errorMessage });
      throw error;
    }
  };

  // Login vía Google Sign-In (CLAUDE.md §2). idToken viene del botón de Google
  // renderizado en Login.tsx; el backend valida el token, prueba el email
  // contra la base de AR y luego la de UY (Phase 1B, CLAUDE.md §1) y
  // devuelve el mismo tipo de access token que el login por contraseña -
  // salvo para el raro caso de doble cuenta (~1-2 personas), donde en cambio
  // devuelve las opciones de país para que Login.tsx muestre la pantalla de
  // selección y llame a selectCountryAfterGoogleLogin con la elección.
  const loginWithGoogle = async (idToken: string): Promise<GoogleCountrySelectionOption[] | void> => {
    dispatch({ type: 'LOGIN_START' });
    try {
      const result = await apiService.auth.loginWithGoogle({ id_token: idToken });

      if (isCountrySelectionRequired(result)) {
        dispatch({ type: 'LOGIN_AWAITING_SELECTION' });
        return result.options;
      }

      const token = result.access_token;
      apiService.setAuthToken(token);
      const user = await apiService.auth.getCurrentUser();

      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('token', token);

      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: { user, token }
      });
    } catch (error: any) {
      let errorMessage = 'Error de login con Google';

      if (error.response?.data) {
        const data = error.response.data;
        if (typeof data.detail === 'string') {
          errorMessage = data.detail;
        } else if (data.message) {
          errorMessage = data.message;
        }
      }

      dispatch({ type: 'LOGIN_FAILURE', payload: errorMessage });
      throw error;
    }
  };

  // Second step of the dual-account pick screen (CLAUDE.md §1/§2) - re-posts
  // the same (still-fresh) Google ID token plus the user's country choice.
  const selectCountryAfterGoogleLogin = async (idToken: string, country: string): Promise<void> => {
    dispatch({ type: 'LOGIN_START' });
    try {
      const tokenResponse = await apiService.auth.selectCountryAfterGoogleLogin({ id_token: idToken, country });
      const token = tokenResponse.access_token;

      apiService.setAuthToken(token);
      const user = await apiService.auth.getCurrentUser();

      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('token', token);

      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: { user, token }
      });
    } catch (error: any) {
      let errorMessage = 'Error de login con Google';

      if (error.response?.data) {
        const data = error.response.data;
        if (typeof data.detail === 'string') {
          errorMessage = data.detail;
        } else if (data.message) {
          errorMessage = data.message;
        }
      }

      dispatch({ type: 'LOGIN_FAILURE', payload: errorMessage });
      throw error;
    }
  };

  // Función de registro
  const register = async (userData: RegisterRequest): Promise<void> => {
    dispatch({ type: 'REGISTER_START' });
    try {
      await apiService.auth.register(userData);
      dispatch({ type: 'REGISTER_SUCCESS' });
    } catch (error: any) {
      let errorMessage = 'Error de registro';
      
      if (error.response?.data) {
        const data = error.response.data;
        
        // Si es un array de errores de validación
        if (Array.isArray(data.detail)) {
          errorMessage = data.detail.map((err: any) => err.msg).join(', ');
        }
        // Si es un string simple
        else if (typeof data.detail === 'string') {
          errorMessage = data.detail;
        }
        // Si es un objeto con mensaje
        else if (data.message) {
          errorMessage = data.message;
        }
        // Si es un error de red o conexión
        else if (error.message) {
          errorMessage = error.message;
        }
      }
      
      dispatch({ type: 'REGISTER_FAILURE', payload: errorMessage });
      throw error;
    }
  };

  // Función de logout
  const logout = (): void => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    apiService.setAuthToken(null);
    dispatch({ type: 'LOGOUT' });
  };

  // Re-fetch the logged-in user's own record from the server and refresh
  // both the in-memory context state AND localStorage['user'] together
  // (the bug this fixes: `user` was hydrated once from localStorage at
  // login/mount with no refresh path anywhere, so an admin editing THEIR
  // OWN row via Gestión de Usuarios never saw the change reflected in
  // anything reading `useAuth().user` - e.g. BudgetFormNew.tsx's
  // vendedor-name pre-fill - until they logged out and back in).
  // Targeted call, not an always-refresh-on-mount: the caller
  // (UserManagement's save handler, when `editedUser.id === currentUser.id`)
  // already knows exactly when the current user's own row just changed, so
  // this avoids adding a network round-trip to every app load for a
  // staleness window that otherwise only opens right after that one action.
  // Silently no-ops on failure (e.g. token briefly invalid) rather than
  // surfacing an error for what is, from the user's point of view, a
  // background refresh of data they didn't explicitly ask for - the save
  // that triggered it has already succeeded and shown its own confirmation.
  const refreshUser = async (): Promise<void> => {
    try {
      const user = await apiService.auth.getCurrentUser();
      localStorage.setItem('user', JSON.stringify(user));
      dispatch({ type: 'SET_USER', payload: user });
    } catch (error) {
      // Best-effort - see comment above.
    }
  };

  const value: AuthContextType = {
    user: state.user,
    token: state.token,
    isLoading: state.isLoading,
    error: state.error,
    login,
    loginWithGoogle,
    selectCountryAfterGoogleLogin,
    register,
    logout,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
